import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../../services/authService";
import "../../styles/AdminOrderDetail.css";

const ORDER_STATUSES = [
  "PENDING",
  "PAID",
  "PROCESSING",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
];

export default function AdminOrderDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    const response = await fetch(`/api/admin/orders/${id}`, {
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      setOrder(data);
      setStatus(data.status);
    } else {
      navigate("/admin/orders");
    }
  };

  const updateStatus = async () => {
    setSaving(true);

    const csrf = await getCsrfToken();

    const response = await fetch(`/api/admin/orders/${id}/status`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [csrf.headerName]: csrf.token,
      },
      body: JSON.stringify({ status }),
    });

    if (response.ok) {
      const updated = await response.json();
      setOrder(updated);
      setStatus(updated.status);
    }

    setSaving(false);
  };

  const renderAddress = (address) => {
    if (!address) {
      return <p>{t("adminOrders.noAddress")}</p>;
    }

    return (
      <>
        <p>
          <strong>
            {address.name} {address.lastname1} {address.lastname2 || ""}
          </strong>
        </p>
        <p>
          {address.street}, {address.numberAddress}
          {address.apartment ? `, ${address.apartment}` : ""}
        </p>
        <p>
          {address.zipCode} {address.city}
          {address.province ? `, ${address.province}` : ""}
        </p>
        <p>{address.country}</p>
        {address.phone && (
          <p>
            {t("addresses.phone")}: {address.phone}
          </p>
        )}
      </>
    );
  };

  if (!order) {
    return null;
  }

  return (
    <main className="admin-order-detail-page">
      <section className="admin-order-detail-container">
        <button
          className="admin-order-detail-back"
          onClick={() => navigate("/admin/orders")}
        >
          ← {t("adminOrders.backToOrders")}
        </button>

        <h1>{order.orderNumber}</h1>

        <button
          type="button"
          className="admin-order-pdf-btn"
          onClick={() => window.open(`/api/admin/orders/${order.id}/pdf`, "_blank")}
        >
          {t("adminOrders.downloadPdf")}
        </button>

        <section className="admin-order-detail-summary">
          <div>
            <span>{t("adminOrders.customer")}</span>
            <strong>{order.customerName}</strong>
            <p>{order.customerEmail}</p>
          </div>

          <div>
            <span>{t("adminOrders.date")}</span>
            <strong>{new Date(order.createdAt).toLocaleDateString()}</strong>
          </div>

          <div>
            <span>{t("adminOrders.total")}</span>
            <strong>{Number(order.total).toFixed(2)} €</strong>
          </div>

          <div>
            <span>{t("adminOrders.status")}</span>
            <strong>{t(`orders.status.${order.status}`)}</strong>
          </div>

          <div>
            <span>{t("adminOrders.paymentMethod")}</span>
            <strong>
              {t(`orders.paymentMethods.${order.paymentMethod}`)}
            </strong>
          </div>

          <div>
            <span>{t("adminOrders.paymentStatus")}</span>
            <strong
              className={`admin-payment-status ${order.paymentStatus.toLowerCase()}`}
            >
              {t(`orders.paymentStatusValues.${order.paymentStatus}`)}
            </strong>
          </div>
        </section>

        <section className="admin-order-status-box">
          <h2>{t("adminOrders.changeStatus")}</h2>

          <div className="admin-order-status-actions">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {ORDER_STATUSES.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {t(`orders.status.${statusOption}`)}
                </option>
              ))}
            </select>
          </div>
        </section>

        <div className="admin-order-quick-actions">

          {order.status === "PAID" && (
            <button onClick={() => setStatus("PROCESSING")}>
              {t("adminOrders.markProcessing")}
            </button>
          )}

          {order.status === "PROCESSING" && (
            <button onClick={() => setStatus("SHIPPED")}>
              {t("adminOrders.markShipped")}
            </button>
          )}

          {order.status === "SHIPPED" && (
            <button onClick={() => setStatus("DELIVERED")}>
              {t("adminOrders.markDelivered")}
            </button>
          )}

        </div>

        {status !== order.status && (
          <button
            className="admin-order-save-btn"
            onClick={updateStatus}
            disabled={saving}
          >
            {saving
              ? t("adminOrders.saving")
              : t("adminOrders.saveStatus")}
          </button>
        )}

        <section className="admin-order-addresses">
          <article>
            <h2>{t("adminOrders.shippingAddress")}</h2>
            {renderAddress(order.shippingAddress)}
          </article>

          <article>
            <h2>{t("adminOrders.billingAddress")}</h2>
            {renderAddress(order.billingAddress)}
          </article>
        </section>

        <section className="admin-order-products">
          <h2>{t("adminOrders.products")}</h2>

          <div className="admin-order-items">
            {order.items.map((item) => (
              <article className="admin-order-item" key={item.productId}>
                {item.imageUrl && (
                  <img src={item.imageUrl} alt={item.productName} />
                )}

                <div>
                  <h3>{item.productName}</h3>
                  <p>
                    {item.quantity} x {Number(item.unitPrice).toFixed(2)} €
                  </p>
                </div>

                <strong>{Number(item.subtotal).toFixed(2)} €</strong>
              </article>
            ))}
          </div>
        </section>
      </section>
    </main>
  );
}